import { ReactNode } from 'react'
import {
  useFloating,
  offset,
  flip,
  shift,
  size,
  autoUpdate,
  useDismiss,
  useInteractions,
  FloatingPortal,
  Placement,
} from '@floating-ui/react'

interface FloatingDropdownProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  reference: HTMLElement | null
  children: ReactNode
  placement?: Placement
  matchReferenceWidth?: boolean
  zIndex?: number
}

export const FloatingDropdown = ({
  open,
  onOpenChange,
  reference,
  children,
  placement = 'bottom-start',
  matchReferenceWidth = false,
  zIndex = 999999999,
}: FloatingDropdownProps) => {
  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange,
    placement,
    strategy: 'fixed',
    middleware: [
      offset(4),
      flip({ padding: 8 }),
      shift({ padding: 8 }),
      size({
        padding: 8,
        apply({ availableHeight, availableWidth, rects, elements }) {
          Object.assign(elements.floating.style, {
            maxHeight: `${Math.max(120, availableHeight)}px`,
            maxWidth: `${availableWidth}px`,
            ...(matchReferenceWidth ? { minWidth: `${rects.reference.width}px` } : {}),
          })
        },
      }),
    ],
    whileElementsMounted: autoUpdate,
    elements: { reference },
  })

  const dismiss = useDismiss(context, { outsidePress: true, escapeKey: true })
  const { getFloatingProps } = useInteractions([dismiss])

  if (!open || !reference) {
    return null
  }

  return (
    <FloatingPortal>
      <div
        ref={refs.setFloating}
        style={{ ...floatingStyles, zIndex, overflowY: 'auto' }}
        {...getFloatingProps()}
      >
        {children}
      </div>
    </FloatingPortal>
  )
}
